import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import useToken from '../components/useToken';

const API_URL = 'https://genz-staging.feeltiptop.com/api/mentor';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: { y: 0, opacity: 1 }
};

const LoadingSpinner = () => (
  <div className="flex items-center justify-center min-h-screen">
    <motion.div
      animate={{ rotate: 360 }}
      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
      className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full"
    />
  </div>
);

const Modal = ({ children, onClose }) => (
    <motion.div 
        className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
    >
        <motion.div 
            className="bg-white rounded-lg shadow-xl p-8 w-full max-w-md min-w-0"
            initial={{ y: -50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -50, opacity: 0 }}
            onClick={e => e.stopPropagation()}
        >
            {children}
        </motion.div>
    </motion.div>
);

const AvatarPlaceholder = ({ name = '', className = '' }) => (
  <span className={`inline-block w-16 h-16 md:w-20 md:h-20 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white text-2xl font-bold shadow ${className}`}>
    <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="24" cy="24" r="24" fill="#60A5FA"/>
      <path d="M24 24C27.3137 24 30 21.3137 30 18C30 14.6863 27.3137 12 24 12C20.6863 12 18 14.6863 18 18C18 21.3137 20.6863 24 24 24Z" fill="white"/>
      <path d="M12 36C12 30.4772 16.4772 26 22 26H26C31.5228 26 36 30.4772 36 36V38H12V36Z" fill="white"/>
    </svg>
  </span>
);

// Reusable avatar component
const AvatarOrPlaceholder = ({ src, name, className = '' }) => {
  const [imgError, setImgError] = useState(false);
  if (!src || imgError) {
    return <AvatarPlaceholder name={name} className={className} />;
  }
  return (
    <img
      src={src}
      alt={name}
      className={className}
      onError={() => setImgError(true)}
    />
  );
};

const MenteeDashboard = ({ menteeProfile, token }) => {
  const [activeTab, setActiveTab] = useState('requests');
  const [outgoingRequests, setOutgoingRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mentors, setMentors] = useState([]); // For Find Mentors tab
  const [mentorRequestStates, setMentorRequestStates] = useState({}); // { mentorId: { requesting: bool, message: string } }
  const authHeader = useMemo(() => (token ? { headers: { Authorization: `Bearer ${token}` } } : {}), [token]);
  const [mentorInfo, setMentorInfo] = useState(null);
  const [profile, setProfile] = useState(menteeProfile);
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);

  // Fetch outgoing requests (requests sent by mentee to mentors)
  const fetchOutgoingRequests = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await axios.get('https://genz-staging.feeltiptop.com/api/mentor/mentee/outgoing-requests', authHeader);
      setOutgoingRequests(res.data.outgoing_requests || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch outgoing requests');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch available mentors for Find Mentors tab
  const fetchMentors = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await axios.get('https://genz-staging.feeltiptop.com/api/mentor/list-mentors', authHeader);
      setMentors(res.data.mentors || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch mentors');
    } finally {
      setIsLoading(false);
    }
  };

  // Initial data fetch based on active tab
  useEffect(() => {
    if (activeTab === 'requests') {
      fetchOutgoingRequests();
    } else if (activeTab === 'findMentors') {
      fetchMentors();
    }
  }, [activeTab, token]);

  // Fetch mentor info if mentee has a mentor
  useEffect(() => {
    if (menteeProfile.mentor_id) {
      axios.get(`https://genz-staging.feeltiptop.com/api/mentor/mentor-for-mentee?mentee_id=${menteeProfile.mentee_id}`, authHeader)
        .then(res => setMentorInfo(res.data.mentor))
        .catch(() => setMentorInfo(null));
    } else {
      setMentorInfo(null);
    }
  }, [menteeProfile.mentor_id, menteeProfile.mentee_id, token]);

  // Cancel a mentor request
  const cancelRequest = async (requestId) => {
    try {
      await axios.delete(`https://genz-staging.feeltiptop.com/api/mentor/cancel-request/${requestId}`, authHeader);
      fetchOutgoingRequests(); // Refresh the requests list
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to cancel request');
    }
  };

  // Send mentor request from Find Mentors tab
  const handleRequestMentor = async (mentor_id) => {
    setMentorRequestStates(prev => ({ 
      ...prev, 
      [mentor_id]: { requesting: true, message: '' }
    }));
    
    try {
      await axios.post('https://genz-staging.feeltiptop.com/api/mentor/request-mentor', { 
        mentor_id,
        message: profile.goals || 'I would like you to be my mentor!'
      }, authHeader);
      
      setMentorRequestStates(prev => ({ 
        ...prev, 
        [mentor_id]: { requesting: false, message: 'Request sent!' }
      }));
      
      // Clear message after 3 seconds
      setTimeout(() => {
        setMentorRequestStates(prev => ({ 
          ...prev, 
          [mentor_id]: { requesting: false, message: '' }
        }));
      }, 3000);
      
      // Switch to requests tab to show the new outgoing request
      setTimeout(() => {
        setActiveTab('requests');
        fetchOutgoingRequests();
      }, 1500);
    } catch (err) {
      setMentorRequestStates(prev => ({ 
        ...prev, 
        [mentor_id]: { requesting: false, message: err.response?.data?.error || 'Failed to send request' }
      }));
      
      // Clear error message after 5 seconds
      setTimeout(() => {
        setMentorRequestStates(prev => ({ 
          ...prev, 
          [mentor_id]: { requesting: false, message: '' }
        }));
      }, 5000);
    }
  };

  // Update mentee profile
  const updateProfile = async (e) => {
    e.preventDefault();
    setIsUpdatingProfile(true);
    try {
      await axios.post('https://genz-staging.feeltiptop.com/api/mentor/update-mentee-profile', {
        goals: profile.goals
      }, authHeader);
      setError(null);
      // Update local profile state
      setProfile(prev => ({ ...prev, goals: profile.goals }));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update profile');
    } finally {
      setIsUpdatingProfile(false);
    }
  };

  // Only show Find Mentors tab if mentee does not have a mentor assigned
  const showFindMentors = !menteeProfile.mentor_id;

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'text-yellow-600 bg-yellow-100';
      case 'approved': return 'text-green-600 bg-green-100';
      case 'rejected': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="mb-8"
        >
          <h1 className="text-3xl font-bold text-gray-900">Mentee Dashboard</h1>
          <p className="mt-2 text-gray-600">Manage your mentor requests and profile</p>
        </motion.div>

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-8">
          <nav className="-mb-px flex space-x-8">
            {['requests', ...(showFindMentors ? ['findMentors'] : []), 'profile'].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`
                  whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
                  ${activeTab === tab
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
                `}
              >
                {tab === 'findMentors' ? 'Find Mentors' : 
                 tab === 'requests' ? 'My Requests' :
                 tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </nav>
        </div>

        {/* Error Message */}
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md"
            >
              <p className="text-red-600">{error}</p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Content */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="bg-white rounded-lg shadow"
        >
          {/* My Requests Tab - Outgoing requests sent by mentee */}
          {activeTab === 'requests' && (
            <motion.div variants={itemVariants} className="p-6">
              <h3 className="text-lg font-medium mb-4">My Mentor Requests</h3>
              <p className="text-sm text-gray-600 mb-4">These are requests you've sent to mentors with your goals as the message.</p>
              <div className="space-y-4">
                {isLoading && <p>Loading...</p>}
                {!isLoading && outgoingRequests.length === 0 && (
                  <div className="text-center py-8">
                    <p className="text-gray-500">You haven't sent any mentor requests yet.</p>
                    {showFindMentors && (
                      <button
                        onClick={() => setActiveTab('findMentors')}
                        className="mt-2 text-blue-600 hover:text-blue-800"
                      >
                        Find mentors to connect with →
                      </button>
                    )}
                  </div>
                )}
                {!isLoading && outgoingRequests.map(request => (
                  <motion.div
                    key={request.request_id}
                    variants={itemVariants}
                    className="bg-white border rounded-lg p-6 shadow-sm relative"
                  >
                    {/* Cancel button - only show for pending requests */}
                    {request.status === 'pending' && (
                      <button
                        onClick={() => cancelRequest(request.request_id)}
                        className="absolute top-2 right-2 text-red-500 hover:text-red-700 focus:outline-none text-xl"
                        title="Cancel Request"
                      >
                        ×
                      </button>
                    )}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        {request.ProfilePictureURL ? (
                          <AvatarOrPlaceholder src={request.ProfilePictureURL} name={request.firstName} className="w-12 h-12 rounded-full" />
                        ) : (
                          <AvatarPlaceholder name={request.firstName} />
                        )}
                        <div>
                          <h3 className="font-medium text-gray-900">
                            {request.firstName || request.lastName
                              ? `${request.firstName || ''} ${request.lastName || ''}`.trim()
                              : 'Unknown Mentor'}
                          </h3>
                          <p className="text-sm text-gray-500">{request.expertise || 'No expertise listed'}</p>
                          {request.message && (
                            <p className="text-sm text-gray-600 mt-1">Your message: "{request.message}"</p>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(request.status)}`}>
                          {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                        </span>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Find Mentors Tab */}
          {activeTab === 'findMentors' && (
            <motion.div variants={itemVariants} className="p-6">
              <h3 className="text-lg font-medium mb-4">Available Mentors</h3>
              <p className="text-sm text-gray-600 mb-4">Send requests to mentors. Your goals will be included as your message.</p>
              <div className="space-y-4">
                {isLoading && <p>Loading...</p>}
                {!isLoading && mentors.length === 0 && <p>No mentors available.</p>}
                {!isLoading && mentors.map(mentor => (
                  <motion.div
                    key={mentor.mentor_id}
                    variants={itemVariants}
                    className="bg-white border rounded-lg p-4 shadow-sm flex items-center justify-between"
                  >
                    <div className="flex items-center space-x-4">
                      {mentor.ProfilePictureURL ? (
                        <AvatarOrPlaceholder src={mentor.ProfilePictureURL} name={mentor.firstName} className="w-12 h-12 rounded-full" />
                      ) : (
                        <AvatarPlaceholder name={mentor.firstName} />
                      )}
                      <div>
                        <span className="font-semibold">{mentor.firstName} {mentor.lastName}</span>
                        <div className="text-sm text-gray-500">{mentor.expertise}</div>
                        {mentor.bio && (
                          <div className="text-sm text-gray-600 mt-1">{mentor.bio}</div>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col items-end">
                      <button
                        onClick={() => handleRequestMentor(mentor.mentor_id)}
                        disabled={mentorRequestStates[mentor.mentor_id]?.requesting}
                        className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm disabled:opacity-50"
                      >
                        {mentorRequestStates[mentor.mentor_id]?.requesting ? 'Sending...' : 'Send Request'}
                      </button>
                      {mentorRequestStates[mentor.mentor_id]?.message && (
                        <span className={`text-xs mt-1 ${
                          mentorRequestStates[mentor.mentor_id]?.message.includes('sent') ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {mentorRequestStates[mentor.mentor_id]?.message}
                        </span>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <motion.div variants={itemVariants} className="p-6 flex justify-center">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl shadow-xl p-8 w-full max-w-lg flex flex-col items-center">
                {/* Show current mentor if assigned */}
                {mentorInfo ? (
                  <div className="mb-4 w-full text-center">
                    <h4 className="font-semibold text-lg text-blue-700 mb-1">Your Mentor</h4>
                    <div className="flex flex-col items-center">
                      {mentorInfo.ProfilePictureURL ? (
                        <AvatarOrPlaceholder src={mentorInfo.ProfilePictureURL} name={mentorInfo.firstName} className="w-16 h-16 rounded-full shadow mb-2 object-cover" />
                      ) : (
                        <AvatarPlaceholder name={mentorInfo.firstName} />
                      )}
                      <span className="font-bold text-gray-900">{mentorInfo.firstName} {mentorInfo.lastName}</span>
                      {mentorInfo.expertise && (
                        <span className="text-sm text-gray-600">{mentorInfo.expertise}</span>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="mb-4 w-full text-center text-gray-500">No mentor assigned.</div>
                )}

                {/* Mentee Profile */}
                {profile.ProfilePictureURL ? (
                  <AvatarOrPlaceholder src={profile.ProfilePictureURL} name={profile.firstName} className="w-24 h-24 rounded-full shadow mb-4 object-cover" />
                ) : (
                  <AvatarPlaceholder name={profile.firstName} />
                )}
                <h3 className="font-bold text-2xl text-gray-900 mb-2">{profile.firstName} {profile.lastName}</h3>
                
                {/* Editable Goals Section */}
                <form onSubmit={updateProfile} className="w-full mt-4">
                  <div className="w-full text-center">
                    <h4 className="font-semibold text-lg text-blue-700 mb-2">Your Goals</h4>
                    <p className="text-sm text-gray-600 mb-2">These will be sent as your message when requesting mentors</p>
                    <textarea
                      value={profile.goals}
                      onChange={(e) => setProfile(prev => ({ ...prev, goals: e.target.value }))}
                      className="w-full text-gray-700 text-base bg-white rounded-lg p-4 shadow-inner border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 resize-none"
                      rows={4}
                      placeholder="Describe your goals and what you hope to achieve with mentorship. This message will be sent to potential mentors..."
                    />
                  </div>
                  
                  <div className="flex justify-center mt-6">
                    <button
                      type="submit"
                      disabled={isUpdatingProfile}
                      className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                    >
                      {isUpdatingProfile ? 'Updating...' : 'Update Goals'}
                    </button>
                  </div>
                </form>
              </div>
            </motion.div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

const MentorDashboard = () => {
  const { token } = useToken();
  const [isMentor, setIsMentor] = useState(null);
  const [isMentee, setIsMentee] = useState(null);
  const [menteeProfile, setMenteeProfile] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [mentees, setMentees] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [profile, setProfile] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // States for modals
  const [showBecomeMentorModal, setShowBecomeMentorModal] = useState(false);
  const [showBecomeMenteeModal, setShowBecomeMenteeModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);

  // States for forms
  const [mentorFormData, setMentorFormData] = useState({ bio: '', expertise: '', max_mentees: 5 });
  const [menteeFormData, setMenteeFormData] = useState({ goals: '' });

  const [allMentors, setAllMentors] = useState([]);
  const [expandedMentorId, setExpandedMentorId] = useState(null);
  const [mentorMentees, setMentorMentees] = useState({}); // mentorId -> mentees

  const [menteeAssignedMentorId, setMenteeAssignedMentorId] = useState(null);
  const [mentorRequestStates, setMentorRequestStates] = useState({}); // { mentorId: { requesting: bool, message: string } }

  const authHeader = useMemo(() => (token ? { headers: { Authorization: `Bearer ${token}` } } : {}), [token]);

  const fetchDashboardData = async () => {
    try {
      setIsLoading(true);
      const profileRes = await axios.get(`${API_URL}/profile`, authHeader);
      setProfile(profileRes.data);
      const [menteesRes, requestsRes] = await Promise.all([
        axios.get(`${API_URL}/list-mentees`, authHeader),
        axios.get(`${API_URL}/pending-requests`, authHeader)
      ]);
      setMentees(menteesRes.data.mentees);
      setPendingRequests(requestsRes.data.pending_requests);
      setIsMentor(true);
      setIsMentee(false);
    } catch (err) {
      if (err.response?.status === 404 && err.response?.config.url.includes('/profile')) {
        // Not a mentor, check if mentee
        try {
          const menteeRes = await axios.get(`${API_URL}/mentee-profile`, authHeader);
          setMenteeProfile(menteeRes.data);
          setIsMentee(true);
          setIsMentor(false);
        } catch (menteeErr) {
          if (menteeErr.response?.status === 404) {
            setIsMentor(false);
            setIsMentee(false);
          } else {
            setError(menteeErr.response?.data?.error || 'Failed to fetch mentee profile');
          }
        }
      } else {
        setError(err.response?.data?.error || 'Failed to fetch dashboard data');
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchDashboardData();
    } else {
      setIsLoading(false);
    }
  }, [token]);

  // Fetch all mentors for the mentors tab
  useEffect(() => {
    if (activeTab === 'mentors') {
      axios.get(`${API_URL}/list-mentors`, authHeader)
        .then(res => setAllMentors(res.data.mentors || []));
    }
  }, [activeTab, token]);

  // Fetch mentee's assigned mentor id if user is a mentee
  useEffect(() => {
    if (isMentee && menteeProfile) {
      setMenteeAssignedMentorId(menteeProfile.mentor_id || null);
    }
  }, [isMentee, menteeProfile]);

  // Expand/collapse logic for mentors
  const handleExpandMentor = async (mentorId) => {
    setExpandedMentorId(expandedMentorId === mentorId ? null : mentorId);
    if (!mentorMentees[mentorId]) {
      const res = await axios.get(`${API_URL}/list-mentees?mentor_id=${mentorId}`, authHeader);
      setMentorMentees(prev => ({ ...prev, [mentorId]: res.data.mentees || [] }));
    }
  };

  const handleBecomeMentor = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/become-mentor`, {
        bio: '',
        expertise: '',
        max_mentees: 5
      }, authHeader);
      setShowBecomeMentorModal(false);
      await fetchDashboardData(); // Refetch data to update UI
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to become mentor');
    }
  };
  
  const handleBecomeMentee = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/become-mentee`, menteeFormData, authHeader);
      setShowBecomeMenteeModal(false);
      await fetchDashboardData(); // Refetch data to update UI
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to become mentee');
    }
  };

  const updateProfile = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/update-mentor-profile`, profile, authHeader);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update profile');
    }
  };

  const removeMentee = async (mentee_id) => {
    try {
      await axios.post(`${API_URL}/remove-mentee`, { mentee_id }, authHeader);
      setMentees(prev => prev.filter(m => m.mentee_id !== mentee_id));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to remove mentee');
    }
  };

  // Request mentorship handler
  const handleRequestMentor = async (mentor_id) => {
    setMentorRequestStates(prev => ({ 
      ...prev, 
      [mentor_id]: { requesting: true, message: '' }
    }));
    
    try {
      await axios.post(`${API_URL}/request-mentor`, { mentor_id }, authHeader);
      setMentorRequestStates(prev => ({ 
        ...prev, 
        [mentor_id]: { requesting: false, message: 'Request sent!' }
      }));
      
      // Clear message after 3 seconds
      setTimeout(() => {
        setMentorRequestStates(prev => ({ 
          ...prev, 
          [mentor_id]: { requesting: false, message: '' }
        }));
      }, 3000);
    } catch (err) {
      setMentorRequestStates(prev => ({ 
        ...prev, 
        [mentor_id]: { requesting: false, message: err.response?.data?.error || 'Failed to send request' }
      }));
      
      // Clear error message after 5 seconds
      setTimeout(() => {
        setMentorRequestStates(prev => ({ 
          ...prev, 
          [mentor_id]: { requesting: false, message: '' }
        }));
      }, 5000);
    }
  };

  // Handle mentor request action
  const handleMentorRequestAction = async (requestId, action) => {
    try {
      await axios.post(
        'https://genz-staging.feeltiptop.com/api/mentor/handle-mentor-request',
        { request_id: requestId, action },
        authHeader
      );
      await fetchDashboardData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to handle request');
    }
  };

  if (!token) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg text-gray-700">Please log in to access the Mentor Dashboard.</div>
      </div>
    );
  }

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (isMentor === false && isMentee === true && menteeProfile) {
    return <MenteeDashboard menteeProfile={menteeProfile} token={token} />;
  }

  if (isMentor === false) {
    return (
      <>
        <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Join as a Mentor or Mentee</h2>
            <p className="text-gray-600 mb-8">Choose your path and start making a difference.</p>
            <div className="flex space-x-4">
                <button onClick={() => setShowBecomeMentorModal(true)} className="px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg shadow-md hover:bg-blue-700 transition">Become a Mentor</button>
                <button onClick={() => setShowBecomeMenteeModal(true)} className="px-6 py-3 bg-green-600 text-white font-semibold rounded-lg shadow-md hover:bg-green-700 transition">Become a Mentee</button>
            </div>
        </div>

        {/* Modals */}
        <AnimatePresence>
            {showBecomeMentorModal && (
                <Modal onClose={() => setShowBecomeMentorModal(false)}>
                    <h3 className="text-xl font-bold mb-4">Mentor Registration</h3>
                    <form onSubmit={handleBecomeMentor} className="space-y-4">
                        <button 
                            type="submit" 
                            className="w-full px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                        >
                            Become a Mentor
                        </button>
                    </form>
                </Modal>
            )}
            {showBecomeMenteeModal && (
                <Modal onClose={() => setShowBecomeMenteeModal(false)}>
                    <h3 className="text-xl font-bold mb-4">Mentee Registration</h3>
                    <form onSubmit={handleBecomeMentee} className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Your Goals</label>
                            <textarea
                                value={menteeFormData.goals}
                                onChange={(e) => setMenteeFormData(prev => ({ ...prev, goals: e.target.value }))}
                                className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-green-500"
                                rows={4}
                                placeholder="What do you hope to achieve with mentorship?"
                                required
                            />
                        </div>
                        <button type="submit" className="w-full px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700">Become a Mentee</button>
                    </form>
                </Modal>
            )}
        </AnimatePresence>
      </>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <motion.div
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="mb-8"
          >
            <h1 className="text-3xl font-bold text-gray-900">Mentor Dashboard</h1>
            <p className="mt-2 text-gray-600">Manage your mentoring relationships and profile</p>
          </motion.div>

          {/* Tabs */}
          <div className="border-b border-gray-200 mb-8">
            <nav className="-mb-px flex space-x-8">
              {['overview', 'mentees', 'requests', 'profile', 'mentors'].map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`
                    whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
                    ${activeTab === tab
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
                  `}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </nav>
          </div>

          {/* Error Message */}
          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md"
              >
                <p className="text-red-600">{error}</p>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Content */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="bg-white rounded-lg shadow"
          >
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <motion.div variants={itemVariants} className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="bg-blue-50 p-6 rounded-lg">
                    <h3 className="text-lg font-medium text-blue-900">Total Mentees</h3>
                    <p className="mt-2 text-3xl font-bold text-blue-600">{mentees.length}</p>
                  </div>
                  <div className="bg-green-50 p-6 rounded-lg">
                    <h3 className="text-lg font-medium text-green-900">Pending Requests</h3>
                    <p className="mt-2 text-3xl font-bold text-green-600">{pendingRequests.length}</p>
                  </div>
                  <div className="bg-purple-50 p-6 rounded-lg">
                    <h3 className="text-lg font-medium text-purple-900">Availability</h3>
                    <button
                      onClick={() => {}}
                      className={`mt-2 px-4 py-2 rounded-full text-sm font-medium ${
                        profile.is_available
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {profile.is_available ? 'Available' : 'Unavailable'}
                    </button>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Mentees Tab */}
            {activeTab === 'mentees' && (
              <motion.div variants={itemVariants} className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-lg font-medium text-gray-900">My Mentees</h3>
                  <button
                    onClick={() => setShowInviteModal(true)}
                    className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
                  >
                    ✉️ Invite Mentees
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {mentees.map(mentee => (
                    <motion.div
                      key={mentee.mentee_id}
                      variants={itemVariants}
                      className="bg-white border rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow relative"
                    >
                      <button
                        onClick={() => removeMentee(mentee.mentee_id)}
                        className="absolute top-2 right-2 text-red-500 hover:text-red-700 focus:outline-none"
                        title="Remove Mentee"
                      >
                        <span role="img" aria-label="Remove">🗑️</span>
                      </button>
                      <div className="flex items-center space-x-4">
                        {mentee.ProfilePictureURL ? (
                          <AvatarOrPlaceholder src={mentee.ProfilePictureURL} name={mentee.firstName} className="w-12 h-12 rounded-full" />
                        ) : (
                          <AvatarPlaceholder name={mentee.firstName} />
                        )}
                        <div>
                          <h3 className="font-medium text-gray-900">{mentee.firstName} {mentee.lastName}</h3>
                          {mentee.goals && (
                            <p className="text-sm text-gray-500 mt-1">{mentee.goals}</p>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Requests Tab */}
            {activeTab === 'requests' && (
              <motion.div variants={itemVariants} className="p-6">
                <h3 className="text-lg font-medium mb-4">Pending Mentee Requests</h3>
                <div className="space-y-4">
                  {pendingRequests.length === 0 && (
                    <div className="text-center py-8">
                      <p className="text-gray-500">No pending requests.</p>
                    </div>
                  )}
                  {pendingRequests.map(request => (
                    <motion.div
                      key={request.request_id}
                      variants={itemVariants}
                      className="bg-white border rounded-lg p-6 shadow-sm"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          {request.ProfilePictureURL ? (
                            <AvatarOrPlaceholder src={request.ProfilePictureURL} name={request.firstName} className="w-12 h-12 rounded-full" />
                          ) : (
                            <AvatarPlaceholder name={request.firstName} />
                          )}
                          <div>
                            <h3 className="font-medium text-gray-900">
                              {request.firstName || request.lastName
                                ? `${request.firstName || ''} ${request.lastName || ''}`.trim()
                                : 'Unknown'}
                            </h3>
                            <p className="text-sm text-gray-500">{request.goals}</p>
                          </div>
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleMentorRequestAction(request.request_id, 'approve')}
                            className="px-4 py-2 bg-green-100 text-green-800 rounded-md hover:bg-green-200"
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => handleMentorRequestAction(request.request_id, 'reject')}
                            className="px-4 py-2 bg-red-100 text-red-800 rounded-md hover:bg-red-200"
                          >
                            Reject
                          </button>
                        </div>
                      </div>
                      {request.message && (
                        <p className="mt-4 text-gray-600">"{request.message}"</p>
                      )}
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Profile Tab */}
            {activeTab === 'profile' && (
              <motion.div variants={itemVariants} className="p-6 flex justify-center">
                <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl shadow-xl p-8 w-full max-w-lg flex flex-col items-center">
                  {profile.ProfilePictureURL ? (
                    <AvatarOrPlaceholder src={profile.ProfilePictureURL} name={profile.firstName} className="w-24 h-24 rounded-full shadow mb-4 object-cover" />
                  ) : (
                    <AvatarPlaceholder name={profile.firstName} />
                  )}
                  <h3 className="font-bold text-2xl text-gray-900 mb-2">{profile.firstName} {profile.lastName}</h3>
                  <form onSubmit={updateProfile} className="w-full mt-4 space-y-4">
                    <div>
                      <label htmlFor="bio" className="block text-sm font-medium text-gray-700">Bio</label>
                      <textarea
                        id="bio"
                        value={profile.bio || ''}
                        onChange={e => setProfile(prev => ({ ...prev, bio: e.target.value }))}
                        rows={3}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500"
                      />
                    </div>
                    <div>
                      <label htmlFor="expertise" className="block text-sm font-medium text-gray-700">Expertise</label>
                      <textarea
                        id="expertise"
                        value={profile.expertise || ''}
                        onChange={e => setProfile(prev => ({ ...prev, expertise: e.target.value }))}
                        rows={2}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500"
                      />
                    </div>
                    <div>
                      <label htmlFor="max_mentees" className="block text-sm font-medium text-gray-700">Maximum Mentees</label>
                      <input
                        type="number"
                        id="max_mentees"
                        value={profile.max_mentees || 5}
                        onChange={e => setProfile(prev => ({ ...prev, max_mentees: parseInt(e.target.value) }))}
                        min="1"
                        max="20"
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500"
                      />
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="is_available"
                        checked={profile.is_available || false}
                        onChange={e => setProfile(prev => ({ ...prev, is_available: e.target.checked }))}
                        className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                      />
                      <label htmlFor="is_available" className="ml-2 block text-sm text-gray-900">Available for new mentees</label>
                    </div>
                    <div className="flex justify-center mt-6">
                      <button
                        type="submit"
                        className="px-6 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
                      >
                        Save Changes
                      </button>
                    </div>
                  </form>
                </div>
              </motion.div>
            )}

            {/* Mentors Tab */}
            {activeTab === 'mentors' && (
              <motion.div variants={itemVariants} className="p-6">
                <h3 className="text-lg font-medium mb-4">All Mentors</h3>
                <div className="space-y-4">
                  {allMentors.map(mentor => (
                    <div key={mentor.mentor_id} className="bg-white border rounded-lg p-4 shadow-sm">
                      <div className="flex items-center justify-between cursor-pointer" onClick={() => handleExpandMentor(mentor.mentor_id)}>
                        <div className="flex items-center space-x-4">
                          {mentor.ProfilePictureURL ? (
                            <AvatarOrPlaceholder src={mentor.ProfilePictureURL} name={mentor.firstName} className="w-10 h-10 rounded-full" />
                          ) : (
                            <AvatarPlaceholder name={mentor.firstName} />
                          )}
                          <div>
                            <span className="font-semibold">{mentor.firstName} {mentor.lastName}</span>
                            {mentor.expertise && (
                              <div className="text-sm text-gray-500">{mentor.expertise}</div>
                            )}
                          </div>
                        </div>
                        <span className="text-blue-500">{expandedMentorId === mentor.mentor_id ? '▲' : '▼'}</span>
                      </div>
                      {isMentee && !menteeAssignedMentorId && (
                        <div className="mt-2 flex items-center space-x-2">
                          <button
                            onClick={e => { e.stopPropagation(); handleRequestMentor(mentor.mentor_id); }}
                            disabled={mentorRequestStates[mentor.mentor_id]?.requesting}
                            className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm disabled:opacity-50"
                          >
                            {mentorRequestStates[mentor.mentor_id]?.requesting ? 'Requesting...' : 'Request Mentorship'}
                          </button>
                          {mentorRequestStates[mentor.mentor_id]?.message && (
                            <span className={`text-sm ${
                              mentorRequestStates[mentor.mentor_id]?.message.includes('sent') ? 'text-green-600' : 'text-red-600'
                            }`}>
                              {mentorRequestStates[mentor.mentor_id]?.message}
                            </span>
                          )}
                        </div>
                      )}
                      {expandedMentorId === mentor.mentor_id && (
                        <div className="mt-4 pl-8">
                          <h4 className="font-medium mb-2 text-blue-700">Mentees</h4>
                          {mentorMentees[mentor.mentor_id] && mentorMentees[mentor.mentor_id].length > 0 ? (
                            <ul className="space-y-2">
                              {mentorMentees[mentor.mentor_id].map(mentee => (
                                <li key={mentee.mentee_id} className="flex items-center space-x-2">
                                  {mentee.ProfilePictureURL ? (
                                    <AvatarOrPlaceholder src={mentee.ProfilePictureURL} name={mentee.firstName} className="w-8 h-8 rounded-full" />
                                  ) : (
                                    <AvatarPlaceholder name={mentee.firstName} />
                                  )}
                                  <span>{mentee.firstName} {mentee.lastName}</span>
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <p className="text-gray-500">No mentees assigned.</p>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
          </motion.div>
        </div>
      </div>
      <AnimatePresence>
        {showInviteModal && <InviteMenteesModal onClose={() => setShowInviteModal(false)} authHeader={authHeader} />}
      </AnimatePresence>
    </>
  );
};

const InviteMenteesModal = ({ onClose, authHeader }) => {
    const [mentees, setMentees] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [inviteState, setInviteState] = useState({}); // { mentee_user_id: 'sending' | 'sent' | 'error' }

    useEffect(() => {
        axios.get(`${API_URL}/list-unassigned-mentees`, authHeader)
            .then(res => setMentees(res.data.mentees))
            .catch(err => setError('Could not load mentees.'))
            .finally(() => setLoading(false));
    }, [authHeader]);

    const handleInvite = async (menteeUserId) => {
        setInviteState(prev => ({ ...prev, [menteeUserId]: 'sending' }));
        try {
            await axios.post(`${API_URL}/mentor-invite-mentee`, { mentee_user_id: menteeUserId, message: 'I would like to be your mentor!' }, authHeader);
            setInviteState(prev => ({ ...prev, [menteeUserId]: 'sent' }));
        } catch (err) {
            setInviteState(prev => ({ ...prev, [menteeUserId]: 'error' }));
        }
    }

    const filteredMentees = mentees.filter(mentee => 
        mentee.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        mentee.lastName?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <Modal onClose={onClose}>
            <h3 className="text-xl font-bold mb-4">Invite Mentees</h3>
            <p className="text-gray-600 mb-4">Search for available mentees and send them an invitation to connect.</p>
            <input 
                type="text"
                placeholder="Search by name..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="w-full p-2 border rounded mb-4"
            />
            <div className="space-y-3 max-h-80 overflow-y-auto">
                {loading && <p>Loading...</p>}
                {error && <p className="text-red-500">{error}</p>}
                {!loading && filteredMentees.map(mentee => (
                    <div key={mentee.user_id} className="flex items-center justify-between p-2 border rounded">
                        <div className="flex items-center">
                            {mentee.ProfilePictureURL ? (
                                <AvatarOrPlaceholder src={mentee.ProfilePictureURL} name={mentee.firstName} className="w-10 h-10 rounded-full mr-3" />
                            ) : (
                                <AvatarPlaceholder name={mentee.firstName} />
                            )}
                            <div>
                                <p className="font-semibold">{mentee.firstName} {mentee.lastName}</p>
                                <p className="text-sm text-gray-500">{mentee.goals?.substring(0, 30)}...</p>
                            </div>
                        </div>
                        <button 
                            onClick={() => handleInvite(mentee.user_id)}
                            disabled={inviteState[mentee.user_id] === 'sending' || inviteState[mentee.user_id] === 'sent'}
                            className={`px-3 py-1 text-sm rounded ${
                                inviteState[mentee.user_id] === 'sent' ? 'bg-gray-400' :
                                inviteState[mentee.user_id] === 'error' ? 'bg-red-500 text-white' :
                                'bg-blue-500 text-white hover:bg-blue-600'
                            } disabled:opacity-50 disabled:cursor-not-allowed`}
                        >
                            {inviteState[mentee.user_id] === 'sending' ? 'Sending...' :
                             inviteState[mentee.user_id] === 'sent' ? 'Invited' :
                             inviteState[mentee.user_id] === 'error' ? 'Retry' : 'Invite'}
                        </button>
                    </div>
                ))}
                {!loading && filteredMentees.length === 0 && <p>No available mentees found.</p>}
            </div>
        </Modal>
    );
}

export default MentorDashboard;